/**
 * Admin Stats → Keyword Database response types (A7Y-4883).
 *
 * Produced by `a7y-api/src/routes/admin/stats.ts`, consumed by
 * `a7y-admin/src/pages/stats/`. Every block is cached and fetched
 * independently; a block answers `ok` with a body or `no-counted-rows` when the
 * data plane holds no `env = 'prod'` keyword rows (staging).
 */

export type KeywordDatabaseStore = 'appstore' | 'playstore';
export type KeywordDatabaseFillDays = 7 | 30 | 90;
export const KEYWORD_DATABASE_FILL_DAYS: readonly KeywordDatabaseFillDays[] = [7, 30, 90];
export type KeywordDatabaseTab = 'overview' | 'fill' | 'markets' | 'sources' | 'coverage';
export const KEYWORD_DATABASE_TABS: readonly KeywordDatabaseTab[] = ['overview', 'fill', 'markets', 'sources', 'coverage'];
/** origin_source of rows A7Y-4785 dates to their Apple list week (Saturday 00:00Z), not to their arrival. */
export const KEYWORD_DATABASE_LIST_WEEK_SOURCE = 'apple_ads.search_term_popularity';

export interface KeywordDatabaseBlockMeta {
  dataset: string;
  generatedAt: string;
  source: 'memory' | 'kv' | 'fresh';
  cacheTtlSec: number;
}
export type KeywordDatabaseBlock<T> =
  | ({ state: 'ok'; body: T } & KeywordDatabaseBlockMeta)
  | ({ state: 'no-counted-rows' } & KeywordDatabaseBlockMeta);

/** The envelope the public keyword-count endpoint's KV tier holds (what a fresh isolate serves). */
export interface KeywordDatabasePublicDisplay {
  keywordCount: number;
  displayCount: number;
  asOf: string;
  generatedAt: string;
}
export interface KeywordDatabaseHeadline {
  total: number;
  asOfDate: string;
  new24h: number;
  /** Of `new24h`, ids whose first row is a list-week-dated `apple_ads.search_term_popularity` row. */
  new24hListWeekDated: number;
  avgDaily7d: number;
  /** The public endpoint's cached figure; null when its KV tier holds none. */
  publicDisplay: KeywordDatabasePublicDisplay | null;
  /** True when the KV tier could not be read — `publicDisplay` is then unknown, not absent. */
  publicDisplayUnreadable?: boolean;
}

export interface KeywordDatabaseQuiet {
  expectedBy: string;
  isQuiet: boolean;
  rule: string;
}
export interface KeywordDatabaseStoreRow {
  store: KeywordDatabaseStore;
  keywords: number;
  markets: number;
  withVolume30d: number;
  tracked: number;
  distinctStrings: number;
  nonLatin: number;
  lastWrittenAt: string;
  quiet: KeywordDatabaseQuiet;
}
export interface KeywordDatabaseOverview {
  stores: KeywordDatabaseStoreRow[];
  /** Distinct term strings across both stores (a string can be in both). */
  distinctStringsTotal: number;
}

export interface KeywordDatabaseHourlyPoint {
  /** `YYYY-MM-DDTHH:00Z` */
  hour: string;
  appstore: number;
  playstore: number;
  /** Subset of `appstore`. */
  listWeekDated: number;
}
export interface KeywordDatabaseDailyRow {
  /** `YYYY-MM-DD` (UTC) */
  day: string;
  appstore: number;
  playstore: number;
  total: number;
  /** Subset of `appstore`. */
  listWeekDated: number;
  runningTotal: number;
}
export interface KeywordDatabaseProjection {
  day0: string;
  day30: string;
  low: number;
  central: number;
  high: number;
  source: string;
}
export const KEYWORD_DATABASE_DAY30_PROJECTION: KeywordDatabaseProjection = {
  day0: '2026-10-03',
  day30: '2026-11-02',
  low: 1_200_000,
  central: 1_600_000,
  high: 2_100_000,
  source: 'A7Y-4796',
};
export interface KeywordDatabaseFill {
  days: KeywordDatabaseFillDays;
  hourly48h: KeywordDatabaseHourlyPoint[];
  daily: KeywordDatabaseDailyRow[];
  projection: KeywordDatabaseProjection;
  rate7d: number;
  daysTo1M: number | null;
}

export interface KeywordDatabaseMarketRow {
  store: KeywordDatabaseStore;
  country: string;
  count: number;
  added24h: number;
  added7d: number;
}
export interface KeywordDatabaseLanguageRow {
  language: string;
  count: number;
  added24h: number;
  added7d: number;
}
export interface KeywordDatabaseMarkets {
  storefronts: KeywordDatabaseMarketRow[];
  languages: KeywordDatabaseLanguageRow[];
}

export interface KeywordDatabaseSourceRow {
  source: string;
  store: KeywordDatabaseStore;
  allTime: number;
  last48h: number;
}
export interface KeywordDatabaseSources {
  rows: KeywordDatabaseSourceRow[];
}

export interface KeywordDatabaseCoverageRow {
  store: KeywordDatabaseStore | 'all';
  total: number;
  withVolume: number;
  withDifficulty: number;
  withSerp: number;
  withApplePopularity: number;
}
export interface KeywordDatabaseCoverage {
  rows: KeywordDatabaseCoverageRow[];
}
